import React, { Component } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { FormattedMessage } from '../../../i18n/FormattedMessage';
import {
  XIcon,
  Maximize2Icon,
  Minimize2Icon,
} from '../../../components_2/AriesIcons';
import AriesSearch from '../../../components_2/AriesSearch';
import ProfilePhoto from './ProfilePhoto';

const GROUP_NAME_MAX_LEN = 25;
const LIMIT = 100;

// Build a safe display name: never emit "undefined"/"null" or a bare space
// when firstName/lastName are missing.
const getFullName = (firstName, lastName) =>
  [firstName, lastName].filter(Boolean).join(' ').trim();

const PersonRow = ({
  firstName,
  lastName,
  subtitle,
  profilePicture,
  id,
  isSelected,
  selectable,
  onClick,
}) => {
  const fullName = getFullName(firstName, lastName);
  return (
    <li className="new-message-row" onClick={() => onClick(id, fullName)}>
      <ProfilePhoto label={fullName} size="36px" src={profilePicture} />
      <div className="new-message-row-info">
        <div className="new-message-row-name">{fullName}</div>
        {subtitle ? (
          <div className="new-message-row-subtitle">{subtitle}</div>
        ) : null}
      </div>
      {selectable && (
        <input
          type="checkbox"
          className="new-message-row-checkbox"
          checked={isSelected}
          readOnly
          tabIndex={-1}
        />
      )}
    </li>
  );
};

PersonRow.propTypes = {
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  subtitle: PropTypes.string,
  profilePicture: PropTypes.string,
  id: PropTypes.string,
  isSelected: PropTypes.bool,
  selectable: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

const PersonTag = ({ firstName, lastName, id, onClick }) => {
  const fullName = getFullName(firstName, lastName);
  return (
    <div className="search-chip" onClick={() => onClick(id)}>
      {fullName}
      <span className="sprite-icon" />
    </div>
  );
};

PersonTag.propTypes = {
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  id: PropTypes.string,
  onClick: PropTypes.func.isRequired,
};

const matchesSearch = (item, term) => {
  if (!term) return true;
  const fullName = getFullName(item.firstName, item.lastName).toLowerCase();
  return fullName.includes(term.toLowerCase());
};

class StartChatModal extends Component {
  initialState = {
    selectedPeople: new Set(),
    groupName: '',
    searchTerm: '',
    isExpanded: false,
  };

  state = { ...this.initialState };

  constructor(props) {
    super(props);
    this.modal = React.createRef();
  }

  componentDidMount() {
    this.props.getAllPeopleToChat(false, 1, LIMIT);
    document.addEventListener('mousedown', this.handleClick);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClick);
  }

  handleClick = (e) => {
    // When the modal is hidden the render returns null, so the ref is empty.
    // Guard against it: nothing to close and no outside-click to handle.
    if (!this.modal.current) {
      return;
    }
    if (this.modal.current.contains(e.target)) {
      return;
    }
    this.props.hideChatModal();
  };

  handlePersonRowClick = (personId, personName) => {
    const {
      isGroupChatEnabled,
      createIndividualChat,
      hideChatModal,
      onAfterCreate,
    } = this.props;
    if (isGroupChatEnabled) {
      this.togglePerson(personId);
    } else {
      // From the header: navigate only after the create request resolves (the
      // reducer sets activeChatId on success), so the chat view opens the new
      // conversation. Within ChatView there is no onAfterCreate (stays as before).
      createIndividualChat(
        { personId, personName },
        onAfterCreate ? this.handleCreateSuccess : undefined
      );
      hideChatModal();
    }
  };

  // Runs after ActionDispatcher resolves (success or failure). The create
  // reducer already updated activeChatId/peopleList before this callback, so we
  // simply hand control to the parent to open the chat view.
  handleCreateSuccess = () => {
    const { onAfterCreate } = this.props;
    if (onAfterCreate) onAfterCreate();
  };

  togglePerson = (personId) => {
    this.setState((prevState) => {
      const selectedPeople = new Set(prevState.selectedPeople);
      if (selectedPeople.has(personId)) {
        selectedPeople.delete(personId);
      } else {
        selectedPeople.add(personId);
      }
      return { selectedPeople };
    });
  };

  deselectPerson = (personId) => {
    this.setState((prevState) => {
      const selectedPeople = new Set(prevState.selectedPeople);
      selectedPeople.delete(personId);
      return { selectedPeople };
    });
  };

  onGroupNameChange = (e) => {
    const value = e.target.value.trimLeft().slice(0, GROUP_NAME_MAX_LEN);
    this.setState({ groupName: value });
  };

  onSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value });
  };

  toggleExpand = () => {
    this.setState((prev) => ({ isExpanded: !prev.isExpanded }));
  };

  createGroupChat = () => {
    const { groupName, selectedPeople } = this.state;
    const {
      createGroupChat,
      currentUser = {},
      hideChatModal,
      onAfterCreate,
    } = this.props;
    createGroupChat(
      {
        groupName,
        members: [...selectedPeople, currentUser.customerId],
      },
      onAfterCreate ? this.handleCreateSuccess : undefined
    );
    hideChatModal();
    this.resetState();
  };

  loadMore = () => {
    this.props.getAllPeopleToChat(true, this.props.page + 1, LIMIT);
  };

  resetState = () => {
    this.setState({ ...this.initialState, selectedPeople: new Set() });
  };

  renderGroupPanel() {
    const { allPeople, intl } = this.props;
    const { groupName, selectedPeople } = this.state;
    return (
      <div className="create-group-chat">
        <p className="new-message-group-hint">
          <FormattedMessage
            id="chat.selectMembersForGroup"
            defaultMessage="Select more than one member for creating group"
          />
        </p>
        <div className="form-group dis-flex f-dir-row">
          <input
            spellCheck="true"
            type="text"
            value={groupName}
            onChange={this.onGroupNameChange}
            placeholder={intl.formatMessage({ id: 'Enter group name' })}
            className="form-control"
            id="usr"
            name="username"
          />
          {groupName && selectedPeople.size >= 1 && (
            <button onClick={this.createGroupChat}>
              <FormattedMessage id={'Create'} defaultMessage={'Create'} />
            </button>
          )}
        </div>
        <div className="chat-person-tag-container">
          {allPeople.map(
            (item) =>
              selectedPeople.has(item._id) && (
                <PersonTag
                  key={item._id}
                  firstName={item.firstName}
                  lastName={item.lastName}
                  id={item._id}
                  onClick={this.deselectPerson}
                />
              )
          )}
        </div>
      </div>
    );
  }

  render() {
    const {
      hideChatModal,
      isGroupChatEnabled,
      allPeople,
      isVisible,
      totalAllPeopleCount,
      intl,
    } = this.props;
    const { selectedPeople, searchTerm, isExpanded } = this.state;
    if (!isVisible) return null;
    const filteredPeople = allPeople.filter((item) =>
      matchesSearch(item, searchTerm)
    );
    return (
      <div className="aries-modal-overlay">
        <dialog
          className={`aries-modal new-message-modal ${
            isExpanded ? 'new-message-modal-expanded' : ''
          }`}
          open
          aria-modal="true"
          ref={this.modal}
        >
          <div className="aries-modal-header">
            <span className="aries-modal-title">
              <FormattedMessage
                id="chat.newMessage"
                defaultMessage="New Message"
              />
            </span>
            <div className="new-message-header-actions">
              <button
                type="button"
                className="aries-modal-close"
                aria-label={intl.formatMessage({
                  id: 'chat.expand',
                  defaultMessage: 'Expand',
                })}
                onClick={this.toggleExpand}
              >
                {isExpanded ? (
                  <Minimize2Icon size={18} />
                ) : (
                  <Maximize2Icon size={18} />
                )}
              </button>
              <button
                type="button"
                className="aries-modal-close"
                aria-label={intl.formatMessage({
                  id: 'chat.close',
                  defaultMessage: 'Close',
                })}
                onClick={hideChatModal}
              >
                <XIcon size={18} />
              </button>
            </div>
          </div>
          <div className="aries-modal-body new-message-body">
            <div className="new-message-search">
              <AriesSearch
                searchValue={searchTerm}
                onChange={this.onSearchChange}
                placeholder="chat.searchMember"
              />
            </div>
            {isGroupChatEnabled && this.renderGroupPanel()}
            <div className="new-message-list">
              <InfiniteScroll
                dataLength={allPeople.length}
                next={this.loadMore}
                hasMore={totalAllPeopleCount > allPeople.length}
                height={isExpanded ? 460 : 320}
              >
                <ul className="new-message-list-ul">
                  {filteredPeople.map((item) => (
                    <PersonRow
                      key={item._id}
                      firstName={item.firstName}
                      lastName={item.lastName}
                      subtitle={item.jobTitle || item.role}
                      profilePicture={item.profilePicture}
                      id={item._id}
                      selectable={isGroupChatEnabled}
                      isSelected={selectedPeople.has(item._id)}
                      onClick={this.handlePersonRowClick}
                    />
                  ))}
                </ul>
              </InfiniteScroll>
            </div>
          </div>
        </dialog>
      </div>
    );
  }
}

StartChatModal.propTypes = {
  allPeople: PropTypes.array.isRequired,
  createGroupChat: PropTypes.func.isRequired,
  createIndividualChat: PropTypes.func.isRequired,
  currentUser: PropTypes.object.isRequired,
  getAllPeopleToChat: PropTypes.func.isRequired,
  hideChatModal: PropTypes.func.isRequired,
  isGroupChatEnabled: PropTypes.bool.isRequired,
  isVisible: PropTypes.bool.isRequired,
  page: PropTypes.number.isRequired,
  totalAllPeopleCount: PropTypes.number.isRequired,
  intl: PropTypes.object.isRequired,
  onAfterCreate: PropTypes.func,
};

export default injectIntl(StartChatModal);
